import { IndustryServiceLocationPage } from "@/components/pages/IndustryServiceLocationPage";
import { ctaDesc, ctaTitle } from "@/lib/seo";
// Phase Sprint 3 — Industries × Service × Location B2B long-tail combo
// /industries/$slug/$service/$location  (~10 × ~4 × ~30 ≈ 1,200 pages)
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  ArrowRight,
  Phone,
  Check,
  Sparkles,
  Target,
  ShieldCheck,
  ChevronRight,
  MapPin,
  MessageCircle,
  BadgeCheck,
  Clock,
} from "lucide-react";
import { LeadFormModal } from "@/components/LeadFormModal";
import { speakableSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import {
  getIndustryBySlug,
  INDUSTRY_SOLUTIONS,
  type IndustrySolution,
} from "@/data/industry-solutions";
import type { ServiceId } from "@/data/services";
import type { ServiceHeadMeta } from "@/data/services-head.gen";
import { getLocation, locations, type Location } from "@/data/locations";

const SITE = "https://nyclanguageinstitute.com";

const BUSINESS_HUB_TYPES = new Set(["headquarters", "branch", "expat-hub", "province"]);
export const BUSINESS_HUB_LOCATIONS = locations.filter((l) => BUSINESS_HUB_TYPES.has(l.type));


export const Route = createFileRoute("/industries/$slug/$service/$location")({
  loader: async ({ params }) => {
    const { SERVICE_HEAD } = await import("@/data/services-head.gen");
    const ind = getIndustryBySlug(params.slug);
    if (!ind) throw notFound();
    if (!(params.service in SERVICE_HEAD)) throw notFound();
    if (!ind.relatedServiceIds.includes(params.service as ServiceId)) throw notFound();
    const loc = getLocation(params.location);
    if (!loc || !BUSINESS_HUB_TYPES.has(loc.type)) throw notFound();
    return { ind, service: SERVICE_HEAD[params.service as ServiceId], loc };
  },
  head: ({ loaderData }) => {
    const ind = loaderData?.ind;
    const s = loaderData?.service;
    const loc = loaderData?.loc;
    if (!ind || !s || !loc)
      return {
        meta: [
          {
            title: ctaTitle(
              "Industry × Service × Location — NYC",
              "/industries/$slug/$service/$location",
              "th",
            ),
          },
        ],
      };
    const url = `${SITE}/industries/${ind.slug}/${s.slug}/${loc.slug}`;
    const title = `${s.nameTh} สำหรับ ${ind.name} · ${loc.nameTh} | NYC`;
    const desc = `บริการ ${s.nameTh} เฉพาะทางสำหรับ ${ind.name} ในพื้นที่ ${loc.nameTh} · ${s.turnaround} · มี Account Manager เฉพาะ sector + รับ-ส่งเอกสารถึง ${loc.nameTh} — สอบถามฟรี ไม่มีข้อผูกมัด`;
    const ratingKey = `industries/${ind.slug}/${s.slug}/${loc.slug}`;
    const rating = aggregateRatingNode(ratingKey);

    return {
      meta: [
        { title: ctaTitle(title.slice(0, 60), "/industries/$slug/$service/$location", "th") },
        {
          name: "description",
          content: ctaDesc(desc.slice(0, 158), "/industries/$slug/$service/$location", "th"),
        },
        {
          name: "keywords",
          content: `${ind.keywords}, ${s.keywords.slice(0, 4).join(", ")}, ${loc.nameTh}, ${loc.nameEn}`,
        },
        {
          property: "og:title",
          content: ctaTitle(title, "/industries/$slug/$service/$location", "th"),
        },
        {
          property: "og:description",
          content: ctaDesc(desc, "/industries/$slug/$service/$location", "th"),
        },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "th_TH" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "th-TH", href: url },
        { rel: "alternate", hrefLang: "x-default", href: url },
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: `${s.nameEn} for ${ind.nameEn} in ${loc.nameEn}`,
              description: desc,
              url,
              provider: { "@id": `${SITE}#organization` },
              areaServed: {
                "@type": "Place",
                name: loc.nameTh,
                ...(loc.lat && loc.lng
                  ? { geo: { "@type": "GeoCoordinates", latitude: loc.lat, longitude: loc.lng } }
                  : {}),
              },
              audience: { "@type": "BusinessAudience", audienceType: ind.audience },
              offers: {
                "@type": "Offer",
                priceCurrency: "THB",
                price: s.priceFrom,
                availability: "https://schema.org/InStock",
              },
              aggregateRating: rating,
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "LegalService",
              "@id": `${url}#localbusiness`,
              name: `NYC Legal & Notary — ${s.nameTh} (${ind.name}) ${loc.nameTh}`,
              url,
              telephone: "+66832494999",
              priceRange: "฿฿",
              address: {
                "@type": "PostalAddress",
                streetAddress: loc.address ?? "61 ซอยลาดพร้าว 95",
                addressLocality: loc.nameTh,
                postalCode: loc.postalCode ?? "10310",
                addressCountry: "TH",
              },
              areaServed: { "@type": "Place", name: loc.nameTh },
              openingHours: "Mo-Sa 09:00-18:00",
              aggregateRating: rating,
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Industries",
                  item: `${SITE}/industries`,
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: ind.name,
                  item: `${SITE}/industries/${ind.slug}`,
                },
                {
                  "@type": "ListItem",
                  position: 4,
                  name: s.nameTh,
                  item: `${SITE}/industries/${ind.slug}/${s.slug}`,
                },
                { "@type": "ListItem", position: 5, name: loc.nameTh, item: url },
              ],
            }),
          },
          ...reviewNodes(ratingKey, `${url}#service`, 2).map((r) => ({
            type: "application/ld+json" as const,
            children: JSON.stringify(r),
          })),
        ]),
      ],
    };
  },
  component: IndustryServiceLocationPage,
  notFoundComponent: () => (
    <main className="grid min-h-screen place-items-center bg-navy-deep p-6 text-white">
      <div className="text-center">
        <h1 className="text-3xl font-extrabold">ไม่พบหน้านี้</h1>
        <Link
          to="/industries"
          className="mt-4 inline-block rounded-lg bg-amber-500 px-4 py-2 font-semibold text-navy-deep"
        >
          กลับ Industry Hub
        </Link>
      </div>
    </main>
  ),
});

// Export combos for sitemap
export const INDUSTRY_SERVICE_LOCATION_COMBOS: {
  industry: string;
  service: ServiceId;
  location: string;
}[] = INDUSTRY_SOLUTIONS.flatMap((i) =>
  i.relatedServiceIds.flatMap((sid) =>
    BUSINESS_HUB_LOCATIONS.map((l) => ({ industry: i.slug, service: sid, location: l.slug })),
  ),
);
